import React, { useState } from 'react'

import { useDashboard } from '@/hooks'

import { validateDashboardTitle } from '../../validation'
import { ColorPicker } from '../ColorPicker'
import { IconPicker } from '../IconPicker'

interface RenameProps {
  onClose: () => void
}

const Rename: React.FC<RenameProps> = ({ onClose }) => {
  const dashboardContext = useDashboard()
  const currentDashboard = dashboardContext.currentDashboard
  const { dashboards } = dashboardContext.configuration

  const [title, setTitle] = useState(currentDashboard.title)
  const [description, setDescription] = useState<string>(currentDashboard.description ?? '')
  const [icon, setIcon] = useState<string | undefined>(currentDashboard.icon)
  const [iconColor, setIconColor] = useState<string | undefined>(currentDashboard.iconColor)

  const titleError = validateDashboardTitle(title, dashboards, currentDashboard.slug)
  const isDirty =
    title.trim() !== currentDashboard.title ||
    description !== (currentDashboard.description ?? '') ||
    icon !== currentDashboard.icon ||
    iconColor !== currentDashboard.iconColor
  const canSave = !titleError && isDirty

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSave) return
    const updatedDashboards = dashboards.map((dashboard) =>
      dashboard.slug === currentDashboard.slug
        ? { ...dashboard, title: title.trim(), description, icon, iconColor }
        : dashboard
    )
    dashboardContext.reorderDashboards(updatedDashboards)
    onClose()
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
        <div className="form-control flex flex-col gap-1">
          <label htmlFor="rename-title" className="label">
            <span className="label-text">Dashboard Title *</span>
          </label>
          <input
            id="rename-title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            placeholder={currentDashboard.title}
            className={`input input-bordered text-base border-base-300 w-full focus:outline-none focus:border-primary/30 ${titleError ? 'input-error' : ''}`}
            onFocus={(e) => e.target.select()}
            autoFocus
          />
          {titleError && (
            <span role="alert" className="label-text-alt text-error">
              {titleError}
            </span>
          )}
        </div>

        <div className="form-control flex flex-col gap-1">
          <label htmlFor="rename-description" className="label">
            <span className="label-text">Description (Optional)</span>
          </label>
          <textarea
            id="rename-description"
            rows={3}
            className="textarea textarea-bordered text-base border-base-300 w-full focus:outline-none focus:border-primary/30"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter dashboard description"
          />
        </div>

        <fieldset className="flex flex-col gap-4">
          <legend className="mb-1 text-sm font-semibold">Appearance</legend>
          <div className="form-control flex flex-col gap-1">
            <span className="label label-text">Icon</span>
            <IconPicker value={icon} color={iconColor} onChange={setIcon} />
          </div>
          <div className="form-control flex flex-col gap-1">
            <span className="label label-text">Icon Color</span>
            <ColorPicker value={iconColor} onChange={setIconColor} />
          </div>
        </fieldset>
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-base-300 p-4">
        <button type="button" onClick={onClose} className="btn btn-ghost">
          Cancel
        </button>
        <button type="submit" disabled={!canSave} className="btn btn-primary">
          Save Changes
        </button>
      </div>
    </form>
  )
}

export default Rename
